import React, { useCallback, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import intlTelInput from 'intl-tel-input';

import Input from 'src/components/ui/input';

import { CountryCode, DEFAULT_COUNTRY_CODE, DEFAULT_DIAL_CODE, IS_HEBREW } from 'src/constants';
import { getCountryPhoneMask, getPhoneMask, parseCountryPhone } from 'src/utils';
import type { InputPhoneProps } from './input-phone.props';

import 'intl-tel-input/build/css/intlTelInput.css';
import './input-phone.scss';

const InputPhone = ({
  className,
  countryCode = DEFAULT_COUNTRY_CODE,
  id,
  isDisabled,
  isLabelWithAsterisk = false,
  isValid,
  label,
  labelClassName,
  onBlur,
  onChange,
  onChangeCountryCode,
  onFocus,
  textError = '',
  size,
  value,
}: InputPhoneProps) => {
  const itiRef = useRef<intlTelInput.Plugin>();
  const inputRef = useRef<HTMLInputElement>(null);
  const [phoneMask, setPhoneMask] = useState(getCountryPhoneMask(countryCode));

  const handleChange = useCallback(
    (value: string) => {
      onChange(
        `+${itiRef.current?.getSelectedCountryData().dialCode ?? DEFAULT_DIAL_CODE} ${value}`
      );
    },
    [onChange]
  );

  useEffect(() => {
    const phoneInput = inputRef.current;

    if (!phoneInput) {
      return;
    }

    itiRef.current = intlTelInput(phoneInput, {
      customContainer: 'input-phone__wrapper',
      initialCountry: countryCode,
      preferredCountries: [countryCode],
    });

    const handleCountryChange = () => {
      const countryCode =
        (itiRef.current?.getSelectedCountryData().iso2 as CountryCode) ?? DEFAULT_COUNTRY_CODE;
      setPhoneMask(getCountryPhoneMask(countryCode));
      handleChange('');
      onChangeCountryCode?.(countryCode);
    };

    const stopPropagationTouch = (event: Event) => {
      if ((event.target as HTMLElement)?.closest('.iti__country-list')) {
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
    };

    phoneInput.addEventListener('countrychange', handleCountryChange);
    document.addEventListener('mousedown', stopPropagationTouch, true);

    return () => {
      phoneInput.removeEventListener('countrychange', handleCountryChange);
      document.removeEventListener('mousedown', stopPropagationTouch, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleChange, onChangeCountryCode]);

  return (
    <Input
      className={classNames('input-phone', className)}
      id={id}
      inputMode="numeric"
      isDisabled={isDisabled}
      isLabelWithAsterisk={isLabelWithAsterisk}
      isValid={isValid}
      label={label}
      labelClassName={classNames('input-phone__label', IS_HEBREW && 'label_right', labelClassName)}
      mask={phoneMask}
      maskPlaceholder="X"
      onBlur={onBlur}
      onChange={handleChange}
      onFocus={onFocus}
      renderCustomMaskText={getPhoneMask}
      ref={inputRef}
      size={size}
      textAlign={IS_HEBREW ? 'right' : 'auto'}
      textError={textError}
      value={parseCountryPhone(value).phone}
    />
  );
};

export default React.memo(InputPhone);
